import { ContactsForm } from "@/components/contact/ContactsForm";
import HiddenInput from "@/components/form/components/HiddenInput";
import { Injector } from "@/components/form/Injector";
import AntIcon from "@/components/icons/AntIcon";
import { selectTagsStandardProps } from "@/utils/componentUtils";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { ValidateErrorEntity } from "@rc-component/form/es/interface";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, App as AntApp, Button, Checkbox, Col, Divider, Form, Input, Row, Select, Space } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router";
import {
  DASHBOARD_QUERY_KEYS,
  useCreateAgent,
  useGetAgentEnumerations,
  useSearchAgent,
  useUpdateAgent,
} from "../../../../../common/api/queries";
import { rowGutter } from "../../../../../common/constants";
import { TopAgentType } from "../../../../../common/enums";
import { UUID } from "../../../../../typings/global";
import { useCurrentAgentAccount } from "../../../../auth/hooks";
import { refreshSession } from "../../../../auth/session";
import { AgentDirectoratesSelect } from "../../../../enumerations/components/form/AgentDirectoratesSelect";
import { AgentSelect } from "../../../../enumerations/components/form/AgentSelect";
import { AgentSecondaryStatus, AgentStatus, AgentType, FinancialSector, secondaryAgentTypeTMap } from "../../../enums";
import { AGENT_ROUTE_PATHS } from "../../../paths";
import { Agent, AgentSearchResult, CreateUpdateAgent } from "../../../types";
import {
  convertAgentToCreateUpdateAgent,
  createPartialCreateUpdateAgent,
  formatAgentShortSystemIdNumber,
  isLoanSpecialistDisabled,
  isRealtySpecialistDisabled,
} from "../../../utils";
import { AgentPartnerIdNumber } from "../../fields/AgentPartnerIdNumber";
import { AgentTypeLongNameTag } from "../../tags/AgenTypeLongNameTag";
import { AgentAddressesFormPart } from "./AgentAddressesFormPart";
import { LegalAgentDataFormPart } from "./LegalAgentDataFormPart";
import { NaturalAgentDataFormPart } from "./NaturalAgentDataFormPart";
import { SelfEmployedAgentDataFormPart } from "./SelfEmployedAgentDataFormPart";

interface Props {
  agent?: Agent;
  allowSearch?: boolean;
  onCancelClick?: VoidFunction;
}

const colSpan = 4;

export const AgentForm = ({ agent, allowSearch, onCancelClick }: Props) => {
  const { t } = useTranslation();
  const { notification } = AntApp.useApp();

  const navigate = useNavigate();

  const [parentId, setParentId] = useState<UUID>();
  const [agentSearchData, setAgentSearchData] = useState<AgentSearchResult>();

  const userAccount = useCurrentAgentAccount();

  const queryClient = useQueryClient();
  const { data: agentParents } = useGetAgentEnumerations(agent?.parent?.id ? [agent.parent.id] : []);
  const agentParent = agentParents?.[0];

  const { data: parentsData } = useGetAgentEnumerations(parentId ? [parentId] : []);
  const parent = parentsData?.[0];

  const agentSearch = useSearchAgent();
  const createAgent = useCreateAgent();
  const updateAgent = useUpdateAgent();

  const [form] = Form.useForm<CreateUpdateAgent>();
  const type = Form.useWatch("type", form);
  const directorateId = Form.useWatch("directorateId", form);
  const sectors = Form.useWatch("sectors", form);
  const status = Form.useWatch("status", form);
  const deactivated = Form.useWatch("deactivated", form);

  const showDirectorateAlert = !!agent && agent.directorate?.id !== directorateId;

  useEffect(() => {
    if (agent) {
      const formData = convertAgentToCreateUpdateAgent(agent);

      form.setFieldsValue(formData);
    }
  }, [agent, form]);

  useEffect(() => {
    if (agentSearchData?.data) {
      const { keyword, agentType, data } = agentSearchData;

      if (
        (agentType === AgentType.NATURAL && keyword === form.getFieldValue("personalIdentificationNumber")) ||
        (agentType !== AgentType.NATURAL && keyword === form.getFieldValue("companyRegistrationNumber"))
      ) {
        const formData = form.getFieldsValue();
        form.setFieldsValue({
          ...createPartialCreateUpdateAgent(data),
          parentId: formData.parentId,
          directorateId: formData.directorateId,
          structureIdNumber: formData.structureIdNumber,
          partnerIdNumber: formData.partnerIdNumber,
          canBeGainer: formData.canBeGainer,
          deactivated: formData.deactivated,
        });
      }
    }
  }, [agentSearchData]);

  useEffect(() => {
    if (!agent) {
      form.setFieldValue("directorateId", parent?.directorateId);
    }
  }, [parent, agent]);

  const handleAgentTypeChange = (type: AgentType) => {
    form.setFieldsValue({ canBeGainer: type !== AgentType.NATURAL && status !== AgentStatus.BROKER_REPRESENTATIVE });
  };

  const handleStatusChange = (status: AgentStatus) => {
    if (status === AgentStatus.TIPPER_WITH_CONTRACT || status === AgentStatus.BROKER_WITH_CONTRACT) {
      form.setFieldsValue({ statusEndDate: undefined, nbsRegistrationNumber: undefined });
    }

    if (status === AgentStatus.TIPPER_WITH_CONTRACT || status === AgentStatus.TIPPER_WITHOUT_CONTRACT) {
      form.setFieldsValue({ nbsRegistrationNumber: undefined });
    }

    if (isRealtySpecialistDisabled(status, sectors)) {
      form.setFieldsValue({ realtySpecialist: false });
    }

    if (isLoanSpecialistDisabled(status, sectors)) {
      form.setFieldsValue({ loanSpecialist: false });
    }

    if (status === AgentStatus.BROKER_REPRESENTATIVE) {
      form.setFieldsValue({ canBeGainer: false });
    }
  };

  const handleSectorChange = (sectors: FinancialSector[]) => {
    if (isRealtySpecialistDisabled(status, sectors)) {
      form.setFieldsValue({ realtySpecialist: false });
    }

    if (isLoanSpecialistDisabled(status, sectors)) {
      form.setFieldsValue({ loanSpecialist: false });
    }
  };

  const handleSecondaryStatusChange = (status: AgentSecondaryStatus) => {
    if (status === AgentSecondaryStatus.AGENT_WITH_CONTRACT) {
      form.setFieldsValue({ secondaryStatusEndDate: undefined });
    }
  };

  const handlePinOrCrnSearchOrChange = (pinOrCrn: string) => {
    const type = form.getFieldValue("type") as AgentType;
    form
      .validateFields(type === AgentType.NATURAL ? ["personalIdentificationNumber"] : ["companyRegistrationNumber"])
      .then(async () => {
        const data = await agentSearch.mutateAsync({ keyword: pinOrCrn, agentType: type });
        setAgentSearchData(data);
      })
      .catch(resolveFormValidationError);
  };

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then(async (values) => {
        const processedValues: CreateUpdateAgent = { ...values };

        processedValues.structureIdNumber = processedValues.structureIdNumber
          ? parseInt(processedValues.structureIdNumber.toString())
          : undefined;

        processedValues.sectors = processedValues.sectors ? processedValues.sectors : [];

        processedValues.contacts = processedValues.contacts ?? [];

        processedValues.serviceAddress = Object.values(processedValues.serviceAddress ?? {}).some(
          (data) => data !== undefined,
        )
          ? processedValues.serviceAddress
          : undefined;

        processedValues.correspondenceAddress = Object.values(processedValues.correspondenceAddress ?? {}).some(
          (data) => data !== undefined,
        )
          ? processedValues.correspondenceAddress
          : undefined;

        if (agent) {
          const updatedAgent = await updateAgent.mutateAsync(
            { id: agent.id, object: processedValues },
            { onError: (error) => setErrorsToForm(form, "agent.attrs", error.violations) },
          );

          if (userAccount?.agent.id === updatedAgent.id) {
            refreshSession();
            queryClient.resetQueries({ queryKey: DASHBOARD_QUERY_KEYS.getDashboardWidgetSettings() });
          }

          onCancelClick?.();
        } else {
          const createdAgent = await createAgent.mutateAsync(processedValues, {
            onError: (error) => setErrorsToForm(form, "agent.attrs", error.violations),
          });

          navigate(generatePath(AGENT_ROUTE_PATHS.detail.to, { id: createdAgent.id }));
        }
      })
      .catch((errors: ValidateErrorEntity) => {
        notification.error({
          title: t("common.error"),
          description: t("common.validations.formError"),
        });
        resolveFormValidationError(errors);
      });
  };

  const renderAgentTypePart = () => {
    switch (type) {
      case AgentType.NATURAL:
        return (
          <NaturalAgentDataFormPart
            searchInProgress={agentSearch.isPending}
            onStatusChange={handleStatusChange}
            onSectorChange={handleSectorChange}
            onSecondaryStatusChange={handleSecondaryStatusChange}
            onPinSearchOrChange={allowSearch ? handlePinOrCrnSearchOrChange : undefined}
          />
        );
      case AgentType.SELF_EMPLOYED:
        return (
          <SelfEmployedAgentDataFormPart
            searchInProgress={agentSearch.isPending}
            onStatusChange={handleStatusChange}
            onSectorChange={handleSectorChange}
            onSecondaryStatusChange={handleSecondaryStatusChange}
            onCrnSearchOrChange={allowSearch ? handlePinOrCrnSearchOrChange : undefined}
          />
        );
      case AgentType.LEGAL:
        return (
          <LegalAgentDataFormPart
            agent={agent}
            searchInProgress={agentSearch.isPending}
            onStatusChange={handleStatusChange}
            onSectorChange={handleSectorChange}
            onSecondaryStatusChange={handleSecondaryStatusChange}
            onCrnSearchOrChange={agentSearch ? handlePinOrCrnSearchOrChange : undefined}
          />
        );
      default:
        return;
    }
  };

  return (
    <Form form={form} layout="vertical" name="agentUpdateForm">
      <HiddenInput name="optimisticLockVersion" />

      <Row gutter={rowGutter}>
        <Col span={6}>
          <Form.Item
            name="type"
            label={t("agent.enums.type._label")}
            rules={[validations.notNull]}
            initialValue={AgentType.NATURAL}
          >
            <Select<AgentType>
              {...selectTagsStandardProps(secondaryAgentTypeTMap)}
              options={Object.keys(AgentType).map((type) => ({
                value: type,
                label: <AgentTypeLongNameTag type={AgentType[type as keyof typeof AgentType]} />,
              }))}
              onChange={handleAgentTypeChange}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        {!agent ? (
          <Col span={colSpan}>
            <Form.Item name="parentId" label={t("agent.attrs.parentId")} rules={[validations.notNull]}>
              <AgentSelect optionsProps={{ onlyActive: deactivated ? undefined : true }} onChange={setParentId} />
            </Form.Item>
          </Col>
        ) : (
          <HiddenInput name={"parentId"} />
        )}

        <Col span={colSpan}>
          <Form.Item
            name="directorateId"
            label={t("agent.attrs.directorate")}
            rules={[agentParent?.directorateId || parent?.directorateId ? validations.notNull : validations.none]}
          >
            <AgentDirectoratesSelect allowClear={!agentParent?.directorateId && !parent?.directorateId} />
          </Form.Item>
        </Col>

        {userAccount?.agent?.topAgentType !== TopAgentType.PLUS && (
          <Col span={colSpan}>
            <Form.Item
              name="structureIdNumber"
              label={t("agent.attrs.structureIdNumber")}
              rules={[validations.size(1, 3), validations.numeric]}
            >
              <Injector>
                {(props) => (
                  <Space.Compact block>
                    <Input {...props} />

                    {agent && <Space.Addon className="nowrap">{formatAgentShortSystemIdNumber(agent)}</Space.Addon>}
                  </Space.Compact>
                )}
              </Injector>
            </Form.Item>
          </Col>
        )}

        <Col span={colSpan}>
          <AgentPartnerIdNumber />
        </Col>

        {showDirectorateAlert ? (
          <Col span={24}>
            <Alert title={t("agent.helpers.agentDirectorateChangeWarning")} type="info" showIcon />
          </Col>
        ) : undefined}
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="canBeGainer"
            valuePropName="checked"
            className={showDirectorateAlert ? "form-item-without-label" : undefined}
            rules={[validations.none]}
            initialValue={false}
          >
            <Checkbox disabled={status === AgentStatus.BROKER_REPRESENTATIVE}>{t("agent.attrs.canBeGainer")}</Checkbox>
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="deactivated"
            valuePropName="checked"
            className={showDirectorateAlert ? "form-item-without-label" : undefined}
            rules={[validations.none]}
            initialValue={false}
          >
            <Checkbox>{t("agent.attrs.deactivated")}</Checkbox>
          </Form.Item>
        </Col>
      </Row>

      <Divider titlePlacement="start">{t("agent.titles.basicData")}</Divider>

      {renderAgentTypePart()}

      <Divider titlePlacement="start">{t("agent.titles.addresses")}</Divider>

      <div style={{ maxWidth: 1100 }}>
        <AgentAddressesFormPart
          agentType={type}
          hasCorrespondenceAddress={!!agent?.correspondenceAddress}
          hasServiceAddress={!!agent?.serviceAddress}
        />
      </div>

      <Divider titlePlacement="start">{t("agent.titles.contacts")}</Divider>

      <ContactsForm />

      <div className="margin-top-large">
        <Space>
          <Form.Item noStyle shouldUpdate={(prev, next) => prev.parentId !== next.parentId}>
            <Button type="primary" icon={<AntIcon type="save" />} onClick={handleFormSubmit}>
              {t("common.save")}
            </Button>
          </Form.Item>

          {agent && (
            <Button onClick={onCancelClick} icon={<AntIcon type="close" />}>
              {t("common.cancel")}
            </Button>
          )}
        </Space>
      </div>
    </Form>
  );
};
